import { initializeApp } from 'firebase/app';
import {
  getDatabase,
  ref,
  push,
  set,
  onValue,
  update,
  remove,
  get,
} from 'firebase/database';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

// ===== TV Management Functions =====

/**
 * Add a new TV to Firebase
 * @param {string} userId - User ID
 * @param {object} tvData - TV data (name, branch, status, etc.)
 * @returns {Promise<string>} - TV ID
 */
export const addTV = async (userId, tvData) => {
  try {
    const tvsRef = ref(database, `tvs/${userId}`);
    const newTVRef = push(tvsRef);
    await set(newTVRef, {
      ...tvData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: tvData.status || 'offline',
    });
    return newTVRef.key;
  } catch (error) {
    console.error('Error adding TV:', error);
    throw error;
  }
};

/**
 * Get all TVs for a user (with real-time updates)
 * @param {string} userId - User ID
 * @param {function} callback - Callback function to receive TV data
 * @param {function} errorCallback - Error callback
 */
export const getAllTVs = (userId, callback, errorCallback) => {
  try {
    const tvsRef = ref(database, `tvs/${userId}`);
    onValue(
      tvsRef,
      (snapshot) => {
        const data = snapshot.val();
        const tvArray = data
          ? Object.keys(data).map((key) => ({
              id: key,
              ...data[key],
            }))
          : [];
        callback(tvArray);
      },
      errorCallback
    );
  } catch (error) {
    console.error('Error getting TVs:', error);
    if (errorCallback) errorCallback(error);
  }
};

/**
 * Fetch TVs by storeId(s) from Firebase Realtime Database
 * @param {string|Array<string>} storeIdOrIds - Single store ID or array of store IDs (license_keys)
 * @returns {Promise<Array>} - Array of TVs for the store(s)
 */
export const fetchTVsByStoreId = async (storeIdOrIds) => {
  try {
    const tvRef = ref(database, `tvs`);
    const snapshot = await get(tvRef);
    
    if (snapshot.exists()) {
      const allTVs = snapshot.val();
      
      // Convert single storeId to array for consistent handling
      const storeIds = Array.isArray(storeIdOrIds) ? storeIdOrIds : [storeIdOrIds];
      
      // Filter TVs by storeIds from takeawayList
      const filteredTVs = Object.entries(allTVs)
        .filter(([key, tv]) => {
          // Check if TV's storeId matches any of the provided storeIds
          return storeIds.includes(tv.storeId);
        })
        .map(([key, tv]) => ({ id: key, ...tv }));
      
      return filteredTVs;
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error fetching TVs:', error);
    throw error;
  }
};

/**
 * Update TV data
 * @param {string} userId - User ID
 * @param {string} tvId - TV ID
 * @param {object} tvData - Updated TV data
 */
export const updateTV = async (userId, tvId, tvData) => {
  try {
    const tvRef = ref(database, `tvs/${userId}/${tvId}`);
    await update(tvRef, {
      ...tvData,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error updating TV:', error);
    throw error;
  }
};

/**
 * Delete a TV
 * @param {string} userId - User ID
 * @param {string} tvId - TV ID
 */
export const deleteTV = async (userId, tvId) => {
  try {
    const tvRef = ref(database, `tvs/${userId}/${tvId}`);
    await remove(tvRef);
  } catch (error) {
    console.error('Error deleting TV:', error);
    throw error;
  }
};

/**
 * Assign media to a TV
 * @param {string} userId - User ID
 * @param {string} tvId - TV ID
 * @param {string} mediaUrl - Media URL from S3
 * @param {string} mediaName - Media name
 */
export const assignMediaToTV = async (userId, tvId, mediaUrl, mediaName) => {
  try {
    const tvRef = ref(database, `tvs/${userId}/${tvId}`);
    await update(tvRef, {
      assignedMedia: mediaUrl,
      assignedMediaName: mediaName,
      mediaAssignedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error assigning media to TV:', error);
    throw error;
  }
};

// ===== Menu Management Functions =====

/**
 * Add a new menu to Firebase
 * @param {string} userId - User ID
 * @param {object} menuData - Menu data
 * @returns {Promise<string>} - Menu ID
 */
export const addMenu = async (userId, menuData) => {
  try {
    const menusRef = ref(database, `menus/${userId}`);
    const newMenuRef = push(menusRef);
    await set(newMenuRef, {
      ...menuData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    return newMenuRef.key;
  } catch (error) {
    console.error('Error adding menu:', error);
    throw error;
  }
};

/**
 * Get all menus for a user
 * @param {string} userId - User ID
 * @param {function} callback - Callback function
 * @param {function} errorCallback - Error callback
 */
export const getAllMenus = (userId, callback, errorCallback) => {
  try {
    const menusRef = ref(database, `menus/${userId}`);
    onValue(
      menusRef,
      (snapshot) => {
        const data = snapshot.val();
        const menuArray = data
          ? Object.keys(data).map((key) => ({
              id: key,
              ...data[key],
            }))
          : [];
        callback(menuArray);
      },
      errorCallback
    );
  } catch (error) {
    console.error('Error getting menus:', error);
    if (errorCallback) errorCallback(error);
  }
};

/**
 * Delete a menu
 * @param {string} userId - User ID
 * @param {string} menuId - Menu ID
 */
export const deleteMenu = async (userId, menuId) => {
  try {
    const menuRef = ref(database, `menus/${userId}/${menuId}`);
    await remove(menuRef);
  } catch (error) {
    console.error('Error deleting menu:', error);
    throw error;
  }
};

// ===== Media Management Functions =====

/**
 * Add media record to Firebase
 * @param {string} userId - User ID
 * @param {object} mediaData - Media metadata
 * @returns {Promise<string>} - Media ID
 */
export const addMediaRecord = async (userId, mediaData) => {
  try {
    const mediaRef = ref(database, `media/${userId}`);
    const newMediaRef = push(mediaRef);
    await set(newMediaRef, {
      ...mediaData,
      createdAt: new Date().toISOString(),
    });
    return newMediaRef.key;
  } catch (error) {
    console.error('Error adding media record:', error);
    throw error;
  }
};

/**
 * Get all media records for a user
 * @param {string} userId - User ID
 * @param {function} callback - Callback function
 * @param {function} errorCallback - Error callback
 */
export const getMediaRecords = (userId, callback, errorCallback) => {
  try {
    const mediaRef = ref(database, `media/${userId}`);
    onValue(
      mediaRef,
      (snapshot) => {
        const data = snapshot.val();
        const mediaArray = data
          ? Object.keys(data).map((key) => ({
              id: key,
              ...data[key],
            }))
          : [];
        callback(mediaArray);
      },
      errorCallback
    );
  } catch (error) {
    console.error('Error getting media records:', error);
    if (errorCallback) errorCallback(error);
  }
};

/**
 * Delete media record from Firebase
 * @param {string} userId - User ID
 * @param {string} mediaId - Media ID
 */
export const deleteMediaRecord = async (userId, mediaId) => {
  try {
    const mediaRef = ref(database, `media/${userId}/${mediaId}`);
    await remove(mediaRef);
  } catch (error) {
    console.error('Error deleting media record:', error);
    throw error;
  }
};

/**
 * Generate a pairing code for TV registration
 * @returns {string} - 5-digit pairing code
 */
export const generatePairingCode = () => {
  return Math.floor(10000 + Math.random() * 90000).toString();
};

/**
 * Add client pairing data for a TV
 * @param {string} id - Pairing code (used as key under pairing/)
 * @param {object} tvData - TV metadata to store for pairing
 * @returns {Promise<object>} - Stored tvData
 */
export const addClientDataForParing = async (id, tvData) => {
  try {
    // Set the new TV object in the `pairing` collection with code as the key
    await set(ref(database, `pairing/${id}`), tvData);
    console.log('TV pairing data added successfully with ID', id);
    return tvData;
  } catch (error) {
    console.error('Error adding TV pairing data:', error);
    throw error;
  }
};

/**
 * Update TV status (slides_updated_at timestamp)
 * @param {string} serialNumber - TV serial number (ID)
 * @returns {Promise<void>}
 */
export const updateStatus = async (serialNumber) => {
  try {
    const updates = { slides_updated_at: Date.now() };
    await update(ref(database, `tvs/${serialNumber}`), updates);
    console.log('TV status updated successfully!');
  } catch (error) {
    console.error('Error updating TV status:', error);
    throw error;
  }
};

/**
 * Update TV orientation
 * @param {string} serialNumber - TV serial number (ID)
 * @param {string} value - Orientation value (e.g. 'landscape right', 'landscape left', 'portrait up', 'portrait down')
 * @returns {Promise<void>}
 */
export const updateOrientation = async (serialNumber, value) => {
  try {
    const updates = { orientation: value };
    await update(ref(database, `tvs/${serialNumber}`), updates);
    console.log('TV orientation updated successfully!');
  } catch (error) {
    console.error('Error updating TV orientation:', error);
    throw error;
  }
};

/**
 * Delete a TV by serial number
 * @param {string} clientId - Client ID (not used in path, kept for compatibility)
 * @param {string} serialNumber - TV serial number (ID)
 * @returns {Promise<void>}
 */
export const deleteTVBySerial = async (clientId, serialNumber) => {
  try {
    // Remove the TV object from the `tvs` collection
    await remove(ref(database, `tvs/${serialNumber}`));
    console.log(`TV with ID: ${serialNumber} deleted successfully.`);
  } catch (error) {
    console.error('Error deleting TV:', error);
    throw error;
  }
};

/**
 * Reload/Restart a TV by sending a restart command
 * @param {string} serialNumber - TV serial number (ID)
 * @returns {Promise<void>}
 */
export const reloadTV = async (serialNumber) => {
  try {
    const restartRef = ref(database, `tvs/${serialNumber}/commands/restart`);
    await set(restartRef, true);
    console.log(`Restart command sent to TV: ${serialNumber}`);
  } catch (error) {
    console.error('Error sending restart command:', error);
    throw error;
  }
};

/**
 * Clear cache for a TV by sending a clear_cache command
 * @param {string} serialNumber - TV serial number (ID)
 * @returns {Promise<void>}
 */
export const clearCacheTV = async (serialNumber) => {
  try {
    const clearCacheRef = ref(database, `tvs/${serialNumber}/commands/clear_cache`);
    await set(clearCacheRef, true);
    console.log(`Clear cache command sent to TV: ${serialNumber}`);
  } catch (error) {
    console.error('Error sending clear cache command:', error);
    throw error;
  }
};

/**
 * Update TV name
 * @param {string} serialNumber - TV serial number (ID)
 * @param {string} name - New TV name
 * @returns {Promise<void>}
 */
export const updateTVName = async (serialNumber, name) => {
  try {
    const updates = { name: name };
    await update(ref(database, `tvs/${serialNumber}`), updates);
    console.log('TV Name updated successfully!');
  } catch (error) {
    console.error('Error updating TV name:', error);
    throw error;
  }
};

export default {
  addTV,
  getAllTVs,
  fetchTVsByStoreId,
  updateTV,
  deleteTV,
  assignMediaToTV,
  addMenu,
  getAllMenus,
  deleteMenu,
  addMediaRecord,
  getMediaRecords,
  deleteMediaRecord,
  generatePairingCode,
  addClientDataForParing,
  updateStatus,
  deleteTVBySerial,
  reloadTV,
  clearCacheTV,
  updateTVName,
  updateOrientation,
};

